<template>
  <dx-accordion
    class="mb-2"
    :on-open="loadDetails"
    :is-init-open="initiallyOpen || !!offerDetails"
    allow-text-select
  >
    <template slot="headline">
      <div class="row headline">
        <div class="col-12 col-md-3">
          {{ offer.policyholder.fullName }}
        </div>
        <div class="col-12 col-md-3">
          {{ offer.number }}
        </div>
        <div class="col-12 col-md-3">
          {{ productName(offer) }}
        </div>
        <div class="col-12 col-md-3">
          {{ formatPrice(offer) }}
        </div>
      </div>
    </template>

    <template slot="content">
      <dx-loading-overlay
        v-if="forceReload || (isLoading && !offerDetails)"
      />
      <div
        v-if="!forceReload && offerDetails"
      >
        <div class="row">
          <div
            v-if="offerDetails.editable"
            class="col mt-4 text-right edit-icons"
          >
            <button
              class="mr-2 d-inline-block btn-blank icon icon-edit"
              @click="loadFullOfferAndEdit"
            >
              <span class="visually-hidden">
                {{ $t('updateRecords') }}
              </span>
            </button>

            <button
              class="d-inline-block btn-blank icon icon-delete"
              @click="openRejectionOverlay"
            >
              <span class="visually-hidden">
                {{ $t('partnerPortal.offerReject') }}
              </span>
            </button>
          </div>

          <dx-popup-modal
            ref="rejectionOverlay"
          >
            <h3>{{ $t('partnerPortal.offerReject') }}</h3>
            <p>
              {{ $t('partnerPortal.offerRejectModalText') }}
              <dx-select
                v-model="rejectionReason"
                v-validate.disable="'required'"
                :options="offerRejectOptions"
                class="mt-2 mb-4"
                :error="errors.first('rejectionReason')"
                data-vv-name="rejectionReason"
              />
            </p>
            <div class="d-flex flex-wrap-reverse justify-content-center d-sm-block text-right">
              <span
                class="abort-btn mt-2 mt-sm-0 secondary-link btn-blank"
                @click="closeRejectionOverlay"
              >
                {{ $t('abort') }}
              </span>

              <dx-button
                type="button"
                class="d-inline ml-sm-3"
                :text="$t('partnerPortal.offerReject')"
                small
                @click="confirmOfferRejection"
              />
            </div>
          </dx-popup-modal>
        </div>

        <h5>{{ $t('partnerPortal.offerDetails') }}</h5>

        <div class="row">
          <div class="col-12 col-md-7">
            <ul class="neutral">
              <li>{{ $t('partnerPortal.offerCalc') }}: {{ getFormattedDate(offerDetails.calculatedAt) }}</li>
              <li>{{ $t('partnerPortal.plannedInsuranceStart') }}: {{ getFormattedDate(offerDetails.startOfInsurance) }}</li>
              <li>{{ $t('partnerPortal.product') }}: {{ productName(offerDetails) }}</li>
              <li
                v-if="!offerDetails.hasOpenUnderwriting || offerDetails.documents.length"
                class="mt-4"
              >
                <dx-download-list
                  :items="offerDetails.documents"
                  :items-inline="true"
                  tag-type="h4"
                />
              </li>
            </ul>
          </div>
          <div class="col-12 col-md-5">
            {{ $t('partnerPortal.grossPremium') }}: {{ formatPrice(offerDetails) }}
          </div>
        </div>

        <div class="row flex-row-reverse mt-3">
          <div class="col">
            <dx-button
              v-if="offerDetails.acceptable"
              :small="true"
              :text="$t('partnerPortal.acceptOffer')"
              class="text-right mb-3"
              @click="loadFullOfferAndEdit"
            />
            <dx-button
              v-if="offerDetails.reactivatable"
              :small="true"
              :text="$t('partnerPortal.reactivateOffer')"
              class="text-right mb-3"
              @click="reactivate"
            />

            <div
              v-if="offerDetails.hasOpenUnderwriting"
              class="icon-underwriting mb-3"
            >
              {{ $t('partnerPortal.offerAtUnderwriting') }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </dx-accordion>
</template>

<script>
import {
  DxButton, DxAccordion, DxSelect, DxPopupModal,
} from '@sumcumo/dextra-frontend-component-lib'
import { scrollIntoView } from '~/javascripts/helper/scollIntoView'
import DxDownloadList from '~/components/DX-DownloadList'
import DxLoadingOverlay from '~/components/DX-LoadingOverlay'
import formatOfferPriceMixin from '~/javascripts/mixins/formatOfferPriceMixin'
import dateMixin from '~/javascripts/mixins/dateMixin'

export default {
  name: 'OfferListItem',
  components: {
    DxAccordion,
    DxButton,
    DxDownloadList,
    DxLoadingOverlay,
    DxPopupModal,
    DxSelect,
  },
  mixins: [
    dateMixin,
    formatOfferPriceMixin,
  ],
  props: {
    offer: {
      type: Object,
      default() {
        return {}
      },
    },
    loadOffer: {
      type: Function,
      default: () => null,
    },
    rejectOffer: {
      type: Function,
      default: () => null,
    },
    reactivateOffer: {
      type: Function,
      default: () => null,
    },
    initiallyOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      offerDetails: null,
      rejectionReason: null,
      isLoading: false,
      forceReload: false,
    }
  },
  computed: {
    offerRejectOptions() {
      const defaultOption = {
        value: '',
        label: this.$t('selectOption'),
        caption: true,
      }

      const reasons = [
        'RESIGN',
        'PRODUCT_UNSUITABLE',
        'COMPETITOR_CHEAPER',
        'COMPETITOR_BETTER',
        'OFFER_WITHDRAWN',
      ].map(reason => ({
        value: reason,
        label: this.$t(`partnerPortal.offerRejectionReason.${reason}`),
      }))

      return [defaultOption, ...reasons]
    },
  },
  mounted() {
    if (this.initiallyOpen) {
      scrollIntoView(this.$el)
    }
  },
  methods: {
    async loadFullOfferAndEdit() {
      this.isLoading = true
      try {
        const { productName } = this.offerDetails
        const result = await this.$store.dispatch('EDIT_OFFER', { id: this.offer.id, tariff: productName })
        if (result) {
          this.$router.push({
            path: this.localePath('BrokerOffer-Summary'),
            query: { mode: 'edit' },
          })
        }
      } finally {
        this.isLoading = false
      }
    },
    async loadDetails() {
      this.isLoading = true
      try {
        const result = await this.loadOffer(this.offer.id)
        this.isLoading = false
        if (result) {
          this.offerDetails = result
        }
      } finally {
        this.isLoading = false
        this.forceReload = false
      }
    },

    openRejectionOverlay() {
      this.rejectionReason = null
      this.$refs.rejectionOverlay.open()
    },
    closeRejectionOverlay() {
      this.$refs.rejectionOverlay.close()
    },
    async confirmOfferRejection() {
      const valid = await this.$validator.validate()
      if (valid) {
        this.rejectOffer(this.offer.id, this.rejectionReason, this.closeRejectionOverlay, this.fullReload)
      }
    },
    fullReload() {
      this.forceReload = true
      this.loadDetails()
    },
    reactivate() {
      this.reactivateOffer(this.offer.id, this.fullReload)
    },
    productName(offer) {
      return offer.options || offer.productName
    },
  },
}
</script>

<style lang="scss" scoped>
@import './index';
</style>
